import React from 'react';
import {
  Snackbar,
  Button,
  Box,
  Typography,
  IconButton,
  Slide,
  Paper,
  useTheme,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

function SlideUpTransition(props) {
  return <Slide {...props} direction="up" />;
}

const VersionUpdateNotification = ({
  open,
  onClose,
  onReload,
  serverVersion,
  currentVersion,
  isQueryInProgress = false,
}) => {
  const theme = useTheme();

  // Hide the notification when a query is in progress
  const shouldShow = open && !isQueryInProgress;

  return (
    <Snackbar
      open={shouldShow}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      TransitionComponent={SlideUpTransition}
      ClickAwayListenerProps={{ mouseEvent: false }}
      sx={{
        zIndex: theme.zIndex.drawer + 1,
        bottom: { xs: 12, sm: 16 },
        right: { xs: 12, sm: 16 },
      }}
    >
      <Paper
        elevation={12}
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          borderRadius: 3,
          overflow: 'hidden',
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1a237e 0%, #3949ab 100%)'
            : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
          color: '#fff',
          minWidth: 280,
          maxWidth: 360,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255,255,255,0.1)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
        }}
      >
        {/* Update Icon */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 40,
            height: 40,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.15)',
            margin: 1.5,
            flexShrink: 0,
          }}
        >
          <SystemUpdateAltIcon 
            sx={{ 
              fontSize: 20, 
              color: '#fff',
              filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.2))'
            }} 
          />
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, py: 1.5, pr: 1 }}>
          {/* Title */}
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600, 
              fontSize: '0.875rem',
              lineHeight: 1.2,
              mb: 0.5,
              color: '#fff',
              textShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            New version available
          </Typography>

          {/* Version Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Chip
              label={currentVersion}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 500,
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)',
                '& .MuiChip-label': {
                  px: 1,
                }
              }}
            />
            <ArrowForwardIcon sx={{ fontSize: 16, opacity: 0.8 }} />
            <Chip
              label={serverVersion}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.7rem',
                fontWeight: 600,
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                border: '1px solid rgba(255,255,255,0.4)',
                '& .MuiChip-label': {
                  px: 1,
                }
              }}
            />
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pr: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => onReload(isQueryInProgress)}
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            sx={{
              minWidth: 'auto',
              px: 1.5,
              py: 0.5,
              height: 28,
              fontSize: '0.75rem',
              fontWeight: 600,
              textTransform: 'none',
              background: 'rgba(255,255,255,0.9)',
              color: theme.palette.primary.main,
              borderRadius: 2,
              '&:hover': {
                background: '#fff',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            Update
          </Button>
          
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              width: 24,
              height: 24,
              color: 'rgba(255,255,255,0.7)',
              '&:hover': { 
                color: '#fff', 
                background: 'rgba(255,255,255,0.1)',
                transform: 'scale(1.1)' 
              },
              transition: 'all 0.2s ease',
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      </Paper>
    </Snackbar>
  );
};

export default VersionUpdateNotification;
